/**
 * BU-v2 P3-3 unit switcher — client persistence of the caller's active
 * "create into" operating unit.
 *
 * Kept as a dependency-free localStorage projection (NOT the Zustand UI store)
 * so the API client can read it without an import cycle. Stored per-user
 * ({userId, unitId}) so a shared machine never leaks one user's selection to
 * the next. The backend ALWAYS re-validates the sent unit against the caller's
 * reach — this is a convenience/UX seam, never an authority.
 */
const KEY = 'crm_active_unit';

export interface ActiveUnitSelection {
  userId: string;
  unitId: string;
}

export function readActiveUnit(): ActiveUnitSelection | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ActiveUnitSelection>;
    return parsed?.userId && parsed?.unitId
      ? { userId: parsed.userId, unitId: parsed.unitId }
      : null;
  } catch {
    return null;
  }
}

export function writeActiveUnit(selection: ActiveUnitSelection | null): void {
  try {
    if (selection) localStorage.setItem(KEY, JSON.stringify(selection));
    else localStorage.removeItem(KEY);
  } catch {
    /* storage unavailable (private mode) — the header is optional, so ignore */
  }
}

/** The active unit id for a given user, or null if none / it belongs to another user. */
export function activeUnitForUser(userId: string | undefined | null): string | null {
  if (!userId) return null;
  const sel = readActiveUnit();
  return sel && sel.userId === userId ? sel.unitId : null;
}
